import { useCallback, useEffect, useState } from 'react'
import { LIMITS } from '@weblite/permission'
import { useCountDownToDate } from '@weblite/logic-toolkit'
import { dateWithOffset } from '$logic/client-device/time-offset.service'
import {
  resendCode,
  VerificationSession,
  VerifyError,
  verifyPhone,
} from '$logic/auth'
import {
  getThrottleBlock,
  isThrottleFailure,
  persistThrottleBlock,
  ThrottleBlock,
} from '$logic/auth/throttle'
import { t } from '$i18n'
import { nothing } from '$func/utils'

const ERROR_MAPPER: Record<VerifyError, string> = {
  WRONG_CODE: t('signin.verification.wrongCodeEntered'),
  SERVER_ERROR: t('serverError'),
  CLOSE_VERIFICATION: '',
}

const calculateEndDate = () =>
  LIMITS.verificationCode.resendInSeconds * 1000 + dateWithOffset()

export function useVerifyPhoneForm({
  phone,
  sessionId,
  signInUser,
  onVerified,
  onCloseVerification = nothing,
}: VerificationSession & {
  signInUser?: boolean
  onVerified?: (token: string) => void
  onCloseVerification?: () => unknown
}) {
  const [loading, setLoading] = useState(false)
  const [alertText, setAlertText] = useState('')

  const [endDate, setEndDate] = useState(calculateEndDate())
  const [resendThrottleBlock, setResendThrottleBlock] =
    useState<ThrottleBlock | null>(() => getThrottleBlock('resend', phone))
  const [verificationThrottleBlock, setVerificationThrottleBlock] =
    useState<ThrottleBlock | null>(() => getThrottleBlock('verify', phone))
  const resendThrottleRemainingMillis = useCountDownToDate(
    resendThrottleBlock?.retryUntil ?? 0,
    dateWithOffset,
  )
  const verificationThrottleRemainingMillis = useCountDownToDate(
    verificationThrottleBlock?.retryUntil ?? 0,
    dateWithOffset,
  )

  const remainingMillis = useCountDownToDate(endDate, dateWithOffset)
  const resendBlocked = resendThrottleRemainingMillis > 0
  const verificationThrottled = verificationThrottleRemainingMillis > 0
  const throttleBlock =
    resendThrottleRemainingMillis >= verificationThrottleRemainingMillis
      ? resendThrottleBlock
      : verificationThrottleBlock
  const throttleRemainingMillis = Math.max(
    resendThrottleRemainingMillis,
    verificationThrottleRemainingMillis,
  )

  useEffect(() => {
    setResendThrottleBlock(getThrottleBlock('resend', phone))
    setVerificationThrottleBlock(getThrottleBlock('verify', phone))
  }, [phone])

  useEffect(() => {
    if (
      resendThrottleBlock &&
      resendThrottleBlock.retryUntil <= dateWithOffset()
    ) {
      setResendThrottleBlock(null)
    }
  }, [resendThrottleBlock, resendThrottleRemainingMillis])

  useEffect(() => {
    if (
      verificationThrottleBlock &&
      verificationThrottleBlock.retryUntil <= dateWithOffset()
    ) {
      setVerificationThrottleBlock(null)
    }
  }, [verificationThrottleBlock, verificationThrottleRemainingMillis])

  const handleResendCode = useCallback(async () => {
    if (resendBlocked) return
    setLoading(true)
    try {
      const result = await resendCode(phone, sessionId)
      if (result === 'OK') {
        setEndDate(calculateEndDate())
      } else if (isThrottleFailure(result)) {
        setResendThrottleBlock(
          persistThrottleBlock('resend', phone, result.throttle),
        )
        setAlertText('')
      } else {
        setAlertText(t('signin.verification.resendFailed'))
      }
    } finally {
      setLoading(false)
    }
  }, [phone, sessionId, resendBlocked])

  const handleCodeComplete = useCallback(
    async (code: string) => {
      if (verificationThrottled) return
      setLoading(true)
      try {
        const res = await verifyPhone(phone, sessionId, code, signInUser)
        if (isThrottleFailure(res)) {
          setVerificationThrottleBlock(
            persistThrottleBlock('verify', phone, res.throttle),
          )
          setAlertText('')
        } else if (typeof res !== 'string') {
          onVerified?.(res.token)
        } else if (res === 'CLOSE_VERIFICATION') onCloseVerification()
        else setAlertText(ERROR_MAPPER[res])
      } finally {
        setLoading(false)
      }
    },
    [
      phone,
      sessionId,
      onVerified,
      signInUser,
      onCloseVerification,
      verificationThrottled,
    ],
  )

  const handleCodeChange = useCallback(
    async (code: string) => {
      if (verificationThrottled) return
      if (code.length === LIMITS.verificationCode.length) {
        await handleCodeComplete(code)
      } else setAlertText('')
    },
    [handleCodeComplete, verificationThrottled],
  )

  return {
    handleCodeChange,
    handleResendCode,
    resendBlocked,
    blockRemainingMillis: resendThrottleRemainingMillis,
    verificationThrottled,
    throttleBlock,
    throttleRemainingMillis,
    remainingMillis,
    alertText,
    loading,
  }
}
